import React, { useState, useCallback } from "react";

const EditableField = ({
  label,
  value,
  onSave,
  type = "text",
  options = null,
  saving = false,
  placeholder = "",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const [saveTimeout, setSaveTimeout] = useState(null);

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
    setEditValue(value || "");
  }, [value]);

  const handleSave = useCallback(() => {
    setIsEditing(false);
    onSave(editValue);
  }, [editValue, onSave]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue(value || "");
  }, [value]);

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setEditValue(newValue);

      // Auto-save with 500ms debounce
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }

      const timeout = setTimeout(() => {
        onSave(newValue);
      }, 500);

      setSaveTimeout(timeout);
    },
    [onSave, saveTimeout],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        if (type === "textarea") {
          // Allow multiline for textarea
          return;
        }
        handleSave();
      } else if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel, type],
  );

  React.useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [saveTimeout]);

  // Update editValue when value prop changes
  React.useEffect(() => {
    setEditValue(value || "");
  }, [value]);

  const renderInput = () => {
    const baseClasses =
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200";

    if (type === "textarea") {
      return (
        <textarea
          value={editValue}
          onChange={handleChange}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={`${baseClasses} min-h-[80px] resize-none`}
          placeholder={placeholder || `Add ${label.toLowerCase()}`}
          autoFocus
        />
      );
    }

    if (options) {
      return (
        <select
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
          }}
          onBlur={handleSave}
          className={`${baseClasses} cursor-pointer`}
          autoFocus
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={type}
        value={editValue}
        onChange={handleChange}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={baseClasses}
        placeholder={placeholder || `Add ${label.toLowerCase()}`}
        autoFocus
      />
    );
  };

  return (
    <div>
      <label className="block text-sm font-bold font-display text-gray-700 mb-2">
        {label}
      </label>
      {isEditing ? (
        <div className="relative">
          {renderInput()}
          {saving && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={handleStartEdit}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl cursor-text hover:bg-white/40 min-h-[44px] flex items-center transition-all duration-200 group"
        >
          <span
            className={`${value ? "text-gray-900" : "text-gray-400"} group-hover:text-gray-700`}
          >
            {value || placeholder || `Click to add ${label.toLowerCase()}`}
          </span>
          {saving && (
            <div className="ml-2">
              <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableField;
